import { supabase } from '../lib/supabase';
import { checkCurrentUserPermission } from '../utils/permissionEnforcement';

export const roleService = {
  // Get all system roles with permission counts
  async getAllRoles() {
    // RLS policy 'users_view_system_roles' already allows all authenticated users to view roles
    // No additional permission check needed here

    const { data, error } = await supabase?.from('system_roles')?.select(`
        *,
        role_permissions(count),
        user_role_assignments(count)
      `)?.order('hierarchy_level', { ascending: true });

    if (error) throw error;

    return data?.map(role => ({
      id: role?.id,
      name: role?.name,
      displayName: role?.display_name,
      description: role?.description,
      isSystemRole: role?.is_system_role,
      hierarchyLevel: role?.hierarchy_level,
      overseesRoleIds: role?.oversees_role_ids || [],
      canBeEdited: role?.can_be_edited !== false,
      permissionCount: role?.role_permissions?.[0]?.count || 0,
      userCount: role?.user_role_assignments?.[0]?.count || 0,
      createdAt: role?.created_at,
      updatedAt: role?.updated_at
    }));
  },

  // Get role with full permission details
  async getRoleById(roleId) {
    const { data: role, error: roleError } = await supabase?.from('system_roles')?.select('*')?.eq('id', roleId)?.single();

    if (roleError) throw roleError;

    const { data: permissions, error: permError } = await supabase?.from('role_permissions')?.select('*')?.eq('role_id', roleId);

    if (permError) throw permError;

    return {
      id: role?.id,
      name: role?.name,
      displayName: role?.display_name,
      description: role?.description,
      isSystemRole: role?.is_system_role,
      hierarchyLevel: role?.hierarchy_level,
      overseesRoleIds: role?.oversees_role_ids || [],
      canBeEdited: role?.can_be_edited !== false,
      permissions: permissions?.map(p => ({
        id: p?.id,
        feature: p?.feature,
        accessLevel: p?.access_level
      })),
      createdAt: role?.created_at,
      updatedAt: role?.updated_at
    };
  },

  // Create new role
  async createRole(roleData) {
    // Check permission dynamically
    const hasPermission = await checkCurrentUserPermission('users');
    if (!hasPermission) {
      throw new Error('Access denied: You do not have permission to create roles');
    }

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: tenant } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

    const { data, error } = await supabase?.from('system_roles')?.insert({
        name: roleData?.name,
        display_name: roleData?.displayName,
        description: roleData?.description,
        hierarchy_level: roleData?.hierarchyLevel || 50,
        tenant_id: tenant?.tenant_id,
        created_by: user?.id
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update role
  async updateRole(roleId, updates) {
    // Check permission dynamically
    const hasPermission = await checkCurrentUserPermission('users');
    if (!hasPermission) {
      throw new Error('Access denied: You do not have permission to update roles');
    }

    const updateData = {
      display_name: updates?.displayName,
      description: updates?.description,
      hierarchy_level: updates?.hierarchyLevel
    };

    // Add oversight relationships if provided
    if (updates?.overseesRoleIds !== undefined) {
      updateData.oversees_role_ids = updates?.overseesRoleIds;
    }

    const { data, error } = await supabase?.from('system_roles')?.update(updateData)?.eq('id', roleId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Delete role (only if not system role)
  async deleteRole(roleId) {
    // Check permission dynamically
    const hasPermission = await checkCurrentUserPermission('users');
    if (!hasPermission) {
      throw new Error('Access denied: You do not have permission to delete roles');
    }

    const { error } = await supabase?.from('system_roles')?.delete()?.eq('id', roleId)?.eq('is_system_role', false);

    if (error) throw error;
  },

  // Get all permissions for a role
  async getRolePermissions(roleId) {
    const { data, error } = await supabase?.from('role_permissions')?.select('*')?.eq('role_id', roleId);

    if (error) throw error;

    return data?.map(p => ({
      id: p?.id,
      feature: p?.feature,
      accessLevel: p?.access_level
    }));
  },

  // Update role permissions (batch)
  async updateRolePermissions(roleId, permissions) {
    // Delete existing permissions
    await supabase?.from('role_permissions')?.delete()?.eq('role_id', roleId);

    // Insert new permissions
    const permissionsToInsert = permissions?.map(p => ({
      role_id: roleId,
      feature: p?.feature,
      access_level: p?.accessLevel
    }));

    const { data, error } = await supabase?.from('role_permissions')?.insert(permissionsToInsert)?.select();

    if (error) throw error;
    return data;
  },

  // Get users assigned to a role
  async getUsersByRole(roleId) {
    const { data, error } = await supabase?.from('user_role_assignments')?.select(`
        *,
        user_profiles(id, email, first_name, last_name)
      `)?.eq('role_id', roleId);

    if (error) throw error;

    return data?.map(assignment => ({
      assignmentId: assignment?.id,
      userId: assignment?.user_profiles?.id,
      email: assignment?.user_profiles?.email,
      fullName: `${assignment?.user_profiles?.first_name} ${assignment?.user_profiles?.last_name}`,
      assignedAt: assignment?.assigned_at
    }));
  },

  /**
   * Get all available roles for user assignment
   * @returns {Promise<Array>} Array of role objects
   */
  async getAvailableRoles() {
    try {
      const { data, error } = await supabase?.from('system_roles')?.select('*')?.order('hierarchy_level', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(role => ({
        id: role?.id,
        name: role?.name,
        displayName: role?.display_name,
        description: role?.description,
        hierarchyLevel: role?.hierarchy_level,
        isSystemRole: role?.is_system_role,
        tenantId: role?.tenant_id,
        createdAt: role?.created_at,
        updatedAt: role?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching available roles:', error);
      throw error;
    }
  },

  /**
   * Get user's assigned roles
   * @param {string} userId - User ID to fetch roles for
   * @returns {Promise<Array>} Array of role objects
   */
  async getUserRoles(userId) {
    try {
      const { data, error } = await supabase?.from('user_role_assignments')?.select(`
          id,
          role_id,
          assigned_at,
          system_roles (
            id,
            name,
            display_name,
            description,
            hierarchy_level
          )
        `)?.eq('user_id', userId);

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(assignment => ({
        assignmentId: assignment?.id,
        roleId: assignment?.role_id,
        assignedAt: assignment?.assigned_at,
        role: {
          id: assignment?.system_roles?.id,
          name: assignment?.system_roles?.name,
          displayName: assignment?.system_roles?.display_name,
          description: assignment?.system_roles?.description,
          hierarchyLevel: assignment?.system_roles?.hierarchy_level
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching user roles:', error);
      throw error;
    }
  },

  /**
   * Assign role to user
   * @param {string} userId - User ID to assign role to
   * @param {string} roleId - Role ID to assign
   * @returns {Promise<Object>} Created role assignment
   */
  async assignRoleToUser(userId, roleId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's tenant
      const { data: profile } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', userId)?.single();

      const { data, error } = await supabase?.from('user_role_assignments')?.insert({
          user_id: userId,
          role_id: roleId,
          tenant_id: profile?.tenant_id,
          assigned_by: user?.id
        })?.select()?.single();

      if (error) throw error;

      return {
        assignmentId: data?.id,
        userId: data?.user_id,
        roleId: data?.role_id,
        assignedAt: data?.assigned_at
      };
    } catch (error) {
      console.error('Error assigning role to user:', error);
      throw error;
    }
  },

  /**
   * Remove role from user
   * @param {string} userId - User ID to remove role from
   * @param {string} roleId - Role ID to remove
   * @returns {Promise<void>}
   */
  async removeRoleFromUser(userId, roleId) {
    try {
      const { error } = await supabase?.from('user_role_assignments')?.delete()?.eq('user_id', userId)?.eq('role_id', roleId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing role from user:', error);
      throw error;
    }
  },

  /**
   * Get user's primary role (highest hierarchy)
   * @param {string} userId - User ID to get primary role for
   * @returns {Promise<Object|null>} Primary role object or null
   */
  async getUserPrimaryRole(userId) {
    try {
      const roles = await this.getUserRoles(userId);
      if (!roles || roles?.length === 0) return null;

      // Sort by hierarchy level (lower number = higher priority)
      const sortedRoles = roles?.sort((a, b) => 
        a?.role?.hierarchyLevel - b?.role?.hierarchyLevel
      );

      return sortedRoles?.[0]?.role || null;
    } catch (error) {
      console.error('Error fetching user primary role:', error);
      throw error;
    }
  },

  /**
   * Get roles overseen by a specific role (including nested oversight)
   * @param {string} roleId - Role ID to get overseen roles for
   * @returns {Promise<Array>} Array of overseen role objects
   */
  async getOverseenRoles(roleId) {
    try {
      const { data, error } = await supabase?.rpc('get_overseen_roles', {
        role_uuid: roleId
      });

      if (error) throw error;

      return data?.map(role => ({
        id: role?.role_id,
        name: role?.role_name,
        displayName: role?.role_display_name,
        hierarchyLevel: role?.hierarchy_level,
        depth: role?.depth
      })) || [];
    } catch (error) {
      console.error('Error fetching overseen roles:', error);
      throw error;
    }
  },

  // Get all system features for permission matrix
  getSystemFeatures() {
    return [
      { id: 'accounts', name: 'Accounts', description: 'Manage company accounts and organizations' },
      { id: 'contacts', name: 'Contacts', description: 'Manage individual contacts and relationships' },
      { id: 'deals', name: 'Deals', description: 'Manage sales opportunities and deals' },
      { id: 'reports', name: 'Reports', description: 'View and generate business reports' },
      { id: 'settings', name: 'Settings', description: 'Configure system settings and preferences' },
      { id: 'billing', name: 'Billing', description: 'Manage billing and payment settings' },
      { id: 'users', name: 'Users', description: 'Manage user accounts and permissions' },
      { id: 'integrations', name: 'Integrations', description: 'Configure third-party integrations' },
      { id: 'calendar', name: 'Calendar', description: 'Manage appointments and schedules' },
      { id: 'services', name: 'Services', description: 'Manage service offerings and configurations' },
      { id: 'customers', name: 'Customers', description: 'Manage customer information and history' },
      { id: 'invoices', name: 'Invoices', description: 'Create and manage invoices' },
      { id: 'notifications', name: 'Notifications', description: 'Manage notification settings' },
      { id: 'activities', name: 'Activities', description: 'Track and manage activities' },
      { id: 'pipeline', name: 'Pipeline', description: 'Manage sales pipeline stages' },
      { id: 'emails', name: 'Emails', description: 'Manage email communications' }
    ];
  },

  // Get access level options
  getAccessLevels() {
    return [
      { id: 'none', name: 'No Access', description: 'Cannot access this feature' },
      { id: 'read', name: 'Read', description: 'Can view but not modify' },
      { id: 'write', name: 'Write', description: 'Can create and edit' },
      { id: 'delete', name: 'Delete', description: 'Can create, edit, and delete' },
      { id: 'admin', name: 'Admin', description: 'Full control over feature' }
    ];
  },

  // ===== BULK PERMISSION OPERATIONS =====

  // Bulk assign permissions to multiple roles
  async bulkAssignPermissions(roleIds, permissions) {
    const permissionsToInsert = [];
    
    for (const roleId of roleIds) {
      // Delete existing permissions for each role
      await supabase?.from('role_permissions')?.delete()?.eq('role_id', roleId);
      
      // Prepare new permissions
      for (const perm of permissions) {
        permissionsToInsert?.push({
          role_id: roleId,
          feature: perm?.feature,
          access_level: perm?.accessLevel
        });
      }
    }

    const { data, error } = await supabase?.from('role_permissions')?.insert(permissionsToInsert)?.select();

    if (error) throw error;
    return data;
  },

  // Copy permissions from one role to another
  async copyPermissions(sourceRoleId, targetRoleIds) {
    // Get source role permissions
    const { data: sourcePermissions, error: fetchError } = await supabase?.from('role_permissions')?.select('*')?.eq('role_id', sourceRoleId);

    if (fetchError) throw fetchError;

    const permissionsToInsert = [];
    
    for (const targetRoleId of targetRoleIds) {
      // Delete existing permissions for target role
      await supabase?.from('role_permissions')?.delete()?.eq('role_id', targetRoleId);
      
      // Copy permissions from source
      for (const perm of sourcePermissions) {
        permissionsToInsert?.push({
          role_id: targetRoleId,
          feature: perm?.feature,
          access_level: perm?.access_level
        });
      }
    }

    const { data, error } = await supabase?.from('role_permissions')?.insert(permissionsToInsert)?.select();

    if (error) throw error;
    return data;
  },

  // ===== PERMISSION TEMPLATES =====

  // Get all permission templates
  async getPermissionTemplates() {
    const { data, error } = await supabase?.from('permission_templates')?.select('*')?.order('is_system_template', { ascending: false })?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(template => ({
      id: template?.id,
      name: template?.name,
      description: template?.description,
      templateData: template?.template_data,
      isSystemTemplate: template?.is_system_template,
      createdBy: template?.created_by,
      createdAt: template?.created_at
    }));
  },

  // Create permission template
  async createPermissionTemplate(templateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: tenant } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

    const { data, error } = await supabase?.from('permission_templates')?.insert({
        name: templateData?.name,
        description: templateData?.description,
        template_data: templateData?.templateData,
        created_by: user?.id,
        tenant_id: tenant?.tenant_id
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Apply template to role
  async applyTemplateToRole(roleId, templateId) {
    // Get template
    const { data: template, error: templateError } = await supabase?.from('permission_templates')?.select('*')?.eq('id', templateId)?.single();

    if (templateError) throw templateError;

    // Delete existing permissions
    await supabase?.from('role_permissions')?.delete()?.eq('role_id', roleId);

    // Convert template data to permissions array
    const permissions = Object.entries(template?.template_data || {})?.map(([feature, accessLevel]) => ({
      role_id: roleId,
      feature: feature,
      access_level: accessLevel
    }));

    const { data, error } = await supabase?.from('role_permissions')?.insert(permissions)?.select();

    if (error) throw error;
    return data;
  },

  // Apply template to multiple roles
  async applyTemplateToRoles(roleIds, templateId) {
    // Get template
    const { data: template, error: templateError } = await supabase?.from('permission_templates')?.select('*')?.eq('id', templateId)?.single();

    if (templateError) throw templateError;

    const permissionsToInsert = [];
    
    for (const roleId of roleIds) {
      // Delete existing permissions
      await supabase?.from('role_permissions')?.delete()?.eq('role_id', roleId);
      
      // Convert template data to permissions
      Object.entries(template?.template_data || {})?.forEach(([feature, accessLevel]) => {
        permissionsToInsert?.push({
          role_id: roleId,
          feature: feature,
          access_level: accessLevel
        });
      });
    }

    const { data, error } = await supabase?.from('role_permissions')?.insert(permissionsToInsert)?.select();

    if (error) throw error;
    return data;
  },

  // Delete permission template
  async deletePermissionTemplate(templateId) {
    const { error } = await supabase?.from('permission_templates')?.delete()?.eq('id', templateId)?.eq('is_system_template', false);

    if (error) throw error;
  },

  // Save current role permissions as template
  async saveRoleAsTemplate(roleId, templateName, templateDescription) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: tenant } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

    // Get role permissions
    const { data: permissions, error: permError } = await supabase?.from('role_permissions')?.select('*')?.eq('role_id', roleId);

    if (permError) throw permError;

    // Convert permissions to template format
    const templateData = {};
    permissions?.forEach(perm => {
      templateData[perm?.feature] = perm?.access_level;
    });

    const { data, error } = await supabase?.from('permission_templates')?.insert({
        name: templateName,
        description: templateDescription,
        template_data: templateData,
        created_by: user?.id,
        tenant_id: tenant?.tenant_id
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // ===== PERMISSION VALIDATION =====

  /**
   * Validate role permissions for conflicts
   * @param {string} roleId - Role ID to validate
   * @param {Array} permissions - Array of permission objects {feature, accessLevel}
   * @returns {Promise<Object>} Validation result with conflicts if any
   */
  async validateRolePermissions(roleId, permissions) {
    try {
      // Convert permissions to JSONB format for database function
      const permissionsJson = permissions?.map(p => ({
        feature: p?.feature,
        access_level: p?.accessLevel
      }));

      const { data, error } = await supabase?.rpc('validate_role_permissions', {
        p_role_id: roleId,
        p_new_permissions: permissionsJson
      });

      if (error) throw error;

      return {
        isValid: data?.[0]?.is_valid,
        conflicts: data?.[0]?.conflicts || []
      };
    } catch (error) {
      console.error('Error validating role permissions:', error);
      throw error;
    }
  },

  /**
   * Validate permissions for multiple roles (bulk validation)
   * @param {Array} roleIds - Array of role IDs
   * @param {Array} permissions - Array of permission objects
   * @returns {Promise<Array>} Validation results for each role
   */
  async validateBulkPermissions(roleIds, permissions) {
    try {
      const permissionsJson = permissions?.map(p => ({
        feature: p?.feature,
        access_level: p?.accessLevel
      }));

      const { data, error } = await supabase?.rpc('validate_bulk_permissions', {
        p_role_ids: roleIds,
        p_permissions: permissionsJson
      });

      if (error) throw error;

      return data?.map(result => ({
        roleId: result?.role_id,
        roleName: result?.role_name,
        isValid: result?.is_valid,
        conflicts: result?.conflicts || []
      }));
    } catch (error) {
      console.error('Error validating bulk permissions:', error);
      throw error;
    }
  },

  /**
   * Get all permission conflict rules
   * @returns {Promise<Array>} Array of conflict rule objects
   */
  async getPermissionConflictRules() {
    try {
      const { data, error } = await supabase?.rpc('get_permission_conflict_rules');

      if (error) throw error;

      return data?.map(rule => ({
        id: rule?.id,
        featureA: rule?.feature_a,
        accessLevelA: rule?.access_level_a,
        featureB: rule?.feature_b,
        accessLevelB: rule?.access_level_b,
        conflictType: rule?.conflict_type,
        description: rule?.description
      }));
    } catch (error) {
      console.error('Error fetching permission conflict rules:', error);
      throw error;
    }
  },

  /**
   * Update role permissions with validation
   * @param {string} roleId - Role ID
   * @param {Array} permissions - New permissions to set
   * @returns {Promise<Object>} Updated permissions
   */
  async updateRolePermissionsWithValidation(roleId, permissions) {
    try {
      // First validate the permissions
      const validation = await this.validateRolePermissions(roleId, permissions);

      if (!validation?.isValid) {
        // Format error message with conflict details
        const conflictMessages = validation?.conflicts?.map(c => 
          `${c?.feature1} (${c?.accessLevel1}) conflicts with ${c?.feature2} (${c?.accessLevel2}): ${c?.description}`
        );
        throw new Error(`Permission validation failed:\n${conflictMessages?.join('\n')}`);
      }

      // If validation passes, update permissions
      return await this.updateRolePermissions(roleId, permissions);
    } catch (error) {
      console.error('Error updating role permissions:', error);
      throw error;
    }
  },

  /**
   * Bulk assign permissions with validation
   * @param {Array} roleIds - Array of role IDs
   * @param {Array} permissions - Permissions to assign
   * @returns {Promise<Object>} Assignment result
   */
  async bulkAssignPermissionsWithValidation(roleIds, permissions) {
    try {
      // Validate permissions for all roles
      const validationResults = await this.validateBulkPermissions(roleIds, permissions);

      // Check if any role has validation errors
      const invalidRoles = validationResults?.filter(r => !r?.isValid);
      
      if (invalidRoles?.length > 0) {
        // Format error message with all conflicts
        const errorMessages = invalidRoles?.map(role => {
          const conflicts = role?.conflicts?.map(c => 
            `  - ${c?.feature1} (${c?.accessLevel1}) ↔ ${c?.feature2} (${c?.accessLevel2}): ${c?.description}`
          )?.join('\n');
          return `${role?.roleName}:\n${conflicts}`;
        })?.join('\n\n');

        throw new Error(`Permission validation failed for some roles:\n\n${errorMessages}`);
      }

      // If all validations pass, proceed with bulk assignment
      return await this.bulkAssignPermissions(roleIds, permissions);
    } catch (error) {
      console.error('Error in bulk permission assignment:', error);
      throw error;
    }
  },

  // Get permission differences between multiple roles
  async compareMultipleRoles(roleIds) {
    const rolesData = await Promise.all(
      roleIds?.map(id => this.getRoleById(id))
    );

    const features = this.getSystemFeatures();
    
    // Create permission maps for each role
    const roleMaps = rolesData?.map(role => {
      const permMap = {};
      role?.permissions?.forEach(p => {
        permMap[p?.feature] = p?.accessLevel;
      });
      return {
        id: role?.id,
        name: role?.displayName,
        permissions: permMap
      };
    });

    // Find differences
    const differences = features?.map(feature => {
      const levels = roleMaps?.map(role => role?.permissions?.[feature?.id] || 'none');
      const uniqueLevels = [...new Set(levels)];
      
      return {
        feature: feature?.id,
        featureName: feature?.name,
        hasVariation: uniqueLevels?.length > 1,
        levels: roleMaps?.map((role, index) => ({
          roleName: role?.name,
          level: levels?.[index]
        }))
      };
    });

    return {
      roles: roleMaps?.map(r => ({ id: r?.id, name: r?.name })),
      differences: differences,
      variationCount: differences?.filter(d => d?.hasVariation)?.length
    };
  }
};